import type { Condition } from '@medplum/fhirtypes';

/**
 * The derivations every condition row shares (ENG-5579). Two surfaces can render
 * identical markup and still disagree on which coding is the billing code or
 * which date is "the" date; declaring these once removes that.
 *
 * Untagged shared lib because the consumers straddle the backend/frontend
 * boundary: the api-nest care-plan view model, the patient profile's problem
 * list, and the visit-level Care Plan tab.
 */

export function icd10Code(condition: Condition): string | undefined {
  return condition.code?.coding?.find((coding) =>
    coding.system?.includes('icd-10'),
  )?.code;
}

/**
 * The subset of Condition date fields a row needs. Structural rather than
 * `Condition` so one resolver serves callers holding a FHIR resource (the
 * profile, reading Medplum directly) and callers holding the flattened care-plan
 * wire object — typing it to `Condition` would force a second copy for the wire.
 */
export type ConditionDateFields = {
  onsetDateTime?: string | null;
  onsetPeriod?: { start?: string | null } | null;
  onsetString?: string | null;
  recordedDate?: string | null;
};

export type ConditionDate =
  /** A structured date, safe to format. */
  | { kind: 'onset' | 'recorded'; value: string }
  /**
   * Free-text onset, to render VERBATIM. Date-formatting it would invent
   * precision the finding never had: `new Date('2020')` is Jan 1 2020, and
   * "May 2020" becomes May 1 2020.
   */
  | { kind: 'onsetText'; value: string }
  | { kind: 'none'; value: null };

/**
 * Which clinical date a condition row shows, and whether it reads as an onset or
 * a recording. Onset in any form wins: it is a fact about the patient, while
 * `recordedDate` is only when we wrote it down.
 *
 * `onsetString` is free text ("about 3 months ago", but also "2020") and comes
 * back as its own `onsetText` kind so callers know not to date-format it — the
 * extraction pipeline writes it instead of `onsetDateTime`, and formatting it
 * would invent a day the clinician never stated.
 */
export function conditionDate(fields: ConditionDateFields): ConditionDate {
  const structuredOnset = fields.onsetDateTime ?? fields.onsetPeriod?.start;
  if (structuredOnset) return { kind: 'onset', value: structuredOnset };
  if (fields.onsetString) {
    return { kind: 'onsetText', value: fields.onsetString };
  }
  if (fields.recordedDate) {
    return { kind: 'recorded', value: fields.recordedDate };
  }
  return { kind: 'none', value: null };
}
